import { useCallback, useEffect, useState } from 'react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import type { RowState } from '@internal/backstage-plugin-bidone-backend';
import type { BDirectRow } from '../../../hooks/useBDirectRows';
import {
  useDeleteSection,
  useLockSection,
  useSaveSection,
  useSetItemState,
  useUnlockSection,
} from '../../../hooks/useReleaseChecklist';
import { toChecklistItemInput } from './helpers';

// Per-section drafts, populated when the user clicks Edit and flushed via
// Save / Cancel / Delete. Holds both the editable section title and the
// step rows. Multiple sections can be edited concurrently (different
// backend locks) so we key by sectionId.
interface SectionDraft {
  sectionText: string;
  steps: BDirectRow[];
}
type Drafts = Record<string, SectionDraft>;

// One step row currently open in the modal RTE.
interface EditingStep {
  sectionId: string;
  stepId: string;
}

interface UseDraftArgs {
  release: string;
  items: BDirectRow[];
  currentUser: string | null;
}

interface DraftHandlers {
  drafts: Drafts;
  editingStep: EditingStep | null;
  setEditingStep: React.Dispatch<React.SetStateAction<EditingStep | null>>;

  // Pending flags surfaced for button-disable on the section card. We
  // expose only the booleans the card needs — the underlying mutations
  // stay encapsulated in the hook.
  savePending: boolean;
  cancelPending: boolean;
  deletePending: boolean;
  // The lock mutation's `variables.sectionId` is needed to scope the
  // "Locking…" spinner to the section being locked (other sections
  // shouldn't show a pending state for someone else's click).
  lockPendingSectionId?: string;

  updateDraftSteps: (
    sectionId: string,
    fn: (rows: BDirectRow[]) => BDirectRow[],
  ) => void;
  updateDraftSectionText: (sectionId: string, next: string) => void;

  handleEditSection: (sectionId: string) => Promise<void>;
  handleSaveSection: (sectionId: string) => Promise<void>;
  handleCancelSection: (sectionId: string) => Promise<void>;
  handleDeleteSection: (sectionId: string) => Promise<void>;
  handleChangeState: (itemId: string, next: RowState) => Promise<void>;
}

// Encapsulates the per-section edit lifecycle: lock acquisition, draft
// management, dispatch to save / unlock / delete / state mutations, and
// the auto-re-enter effect that picks up locks held by the current user
// across page reloads.
//
// Returns a stable handler bundle the main component passes straight into
// `BDirectSectionCard` — keeping the orchestrator thin and the side-
// effecting logic in one testable hook.
export const useChecklistDraftHandlers = ({
  release,
  items,
  currentUser,
}: UseDraftArgs): DraftHandlers => {
  const alertApi = useApi(alertApiRef);
  const lockSection = useLockSection();
  const unlockSection = useUnlockSection();
  const saveSection = useSaveSection();
  const deleteSection = useDeleteSection();
  const setItemState = useSetItemState();

  const [drafts, setDrafts] = useState<Drafts>({});
  const [editingStep, setEditingStep] = useState<EditingStep | null>(null);

  // Re-enter edit mode automatically for any section already locked by the
  // current user when the page loads (e.g. a previous tab closed without
  // Save/Cancel). Without this the section's lock persists in storage but
  // the UI shows an unlocked-looking Edit button, leaving the user with no
  // visible way back into their draft. The effect is idempotent — it
  // never overwrites a draft that's already in flight.
  useEffect(() => {
    if (!currentUser || items.length === 0) return;
    setDrafts(prev => {
      let next = prev;
      const sorted = [...items].sort((a, b) => a.order - b.order);
      for (let i = 0; i < sorted.length; i++) {
        const item = sorted[i];
        if (item.kind !== 'section') continue;
        if (item.lockedBy !== currentUser) continue;
        if (next[item.id]) continue;
        const steps: BDirectRow[] = [];
        for (let j = i + 1; j < sorted.length; j++) {
          if (sorted[j].kind === 'section') break;
          steps.push(sorted[j]);
        }
        if (next === prev) next = { ...prev };
        next[item.id] = { sectionText: item.text, steps };
      }
      return next;
    });
  }, [items, currentUser]);

  const updateDraftSteps = useCallback(
    (sectionId: string, fn: (rows: BDirectRow[]) => BDirectRow[]) => {
      setDrafts(prev => {
        const current = prev[sectionId];
        if (!current) return prev;
        return {
          ...prev,
          [sectionId]: { ...current, steps: fn(current.steps) },
        };
      });
    },
    [],
  );

  const updateDraftSectionText = useCallback(
    (sectionId: string, next: string) => {
      setDrafts(prev => {
        const current = prev[sectionId];
        if (!current) return prev;
        return { ...prev, [sectionId]: { ...current, sectionText: next } };
      });
    },
    [],
  );

  const clearDraft = useCallback((sectionId: string) => {
    setDrafts(prev => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  }, []);

  // Acquire the section lock, then seed the draft from the freshly-locked
  // checklist. The lock endpoint returns the raw ChecklistView which lacks
  // the build / owner / perRegionTag decoration that `useBDirectRows`
  // joins in — we copy those over from the already-joined `items` so repo
  // rows keep showing their Azure deployment ticks in edit mode.
  const handleEditSection = useCallback(
    async (sectionId: string) => {
      try {
        const fresh = await lockSection.mutateAsync({ release, sectionId });
        const sorted = [...fresh.items].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex(r => r.id === sectionId);
        if (idx === -1) return;
        const sectionRow = sorted[idx];
        const itemsById = new Map(items.map(i => [i.id, i]));
        const steps: BDirectRow[] = [];
        for (let i = idx + 1; i < sorted.length; i++) {
          if (sorted[i].kind === 'section') break;
          const decorated = itemsById.get(sorted[i].id);
          steps.push({
            ...sorted[i],
            build: decorated?.build,
            component: decorated?.component,
            manualTicks: decorated?.manualTicks ?? false,
            owner: decorated?.owner,
            perRegionTag: decorated?.perRegionTag ?? {},
            perRegionPreprodTag: decorated?.perRegionPreprodTag ?? {},
          });
        }
        setDrafts(prev => ({
          ...prev,
          [sectionId]: { sectionText: sectionRow.text, steps },
        }));
      } catch (e) {
        alertApi.post({
          message: `Failed to lock section: ${(e as Error).message}`,
          severity: 'error',
        });
      }
    },
    [alertApi, items, lockSection, release],
  );

  const handleSaveSection = useCallback(
    async (sectionId: string) => {
      const draft = drafts[sectionId];
      if (!draft) return;
      try {
        await saveSection.mutateAsync({
          release,
          sectionId,
          sectionText: draft.sectionText,
          items: draft.steps.map(toChecklistItemInput),
        });
        clearDraft(sectionId);
        alertApi.post({ message: 'Section saved', severity: 'success' });
      } catch (e) {
        alertApi.post({
          message: `Failed to save section: ${(e as Error).message}`,
          severity: 'error',
        });
      }
    },
    [alertApi, clearDraft, drafts, release, saveSection],
  );

  const handleCancelSection = useCallback(
    async (sectionId: string) => {
      try {
        await unlockSection.mutateAsync({ release, sectionId });
        clearDraft(sectionId);
      } catch (e) {
        alertApi.post({
          message: `Failed to release lock: ${(e as Error).message}`,
          severity: 'error',
        });
      }
    },
    [alertApi, clearDraft, release, unlockSection],
  );

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      try {
        await deleteSection.mutateAsync({ release, sectionId });
        clearDraft(sectionId);
        alertApi.post({ message: 'Section deleted', severity: 'success' });
      } catch (e) {
        alertApi.post({
          message: `Failed to delete section: ${(e as Error).message}`,
          severity: 'error',
        });
      }
    },
    [alertApi, clearDraft, deleteSection, release],
  );

  // State mutates immediately on dropdown change — no save button, no
  // section lock involved (per spec: state is editable outside the
  // section's global Edit mode).
  const handleChangeState = useCallback(
    async (itemId: string, next: RowState) => {
      try {
        await setItemState.mutateAsync({ release, itemId, state: next });
      } catch (e) {
        alertApi.post({
          message: `Failed to update state: ${(e as Error).message}`,
          severity: 'error',
        });
      }
    },
    [alertApi, release, setItemState],
  );

  return {
    drafts,
    editingStep,
    setEditingStep,
    savePending: saveSection.isPending,
    cancelPending: unlockSection.isPending,
    deletePending: deleteSection.isPending,
    lockPendingSectionId: lockSection.isPending
      ? (lockSection.variables?.sectionId as string | undefined)
      : undefined,
    updateDraftSteps,
    updateDraftSectionText,
    handleEditSection,
    handleSaveSection,
    handleCancelSection,
    handleDeleteSection,
    handleChangeState,
  };
};
